// timecode.ts — "display == heard" alignment (docs/PROTOCOL.md §4).
//
// The server stamps each `tc` data packet with `streamSamples` (the running
// count of PCM samples-per-channel it has pushed into the LiveKit audio source)
// plus the corresponding `projectSec` and `playing` flag (docs §2.1).
//
// We want to display the project time of the audio *currently leaving the
// listener's speakers*, not the last packet received. The newest packet
// describes audio that is still sitting in the WebRTC jitter/decode buffer, so
// it is ~playoutDelayMs in the future relative to what is audible right now.
//
// Algorithm (docs §4):
//   1. Keep a linear map streamSamples -> projectSec from recent `tc` packets.
//      While playing, project time advances 1:1 with stream samples; while
//      paused it freezes. We derive the local slope from the packet history.
//   2. Estimate the audible stream-sample position:
//         audibleSamples = receivedStreamSamples - playoutDelaySamples
//      where playoutDelaySamples ~ jitter+decode buffer (default 220 ms,
//      refined from RTCRtpReceiver getStats().jitterBufferDelay when available).
//   3. displayedProjectSec = map(audibleSamples), advanced by the LOCAL clock
//      between packets so the readout is smooth.

export const SAMPLE_RATE = 48_000; // Opus internal rate is always 48 kHz (docs §1.2 / Tier 1).
export const DEFAULT_PLAYOUT_DELAY_MS = 220; // docs §4 default (≈10560 samples @ 48 kHz).

/**
 * Display context (docs §7.7): the format the host chose plus the musical context
 * the client needs to format the *buffer-corrected* (heard) position itself, so
 * the readout stays sync'd to the audio in every unit. `fmt`: 0 = Time,
 * 1 = Bars.Beats, 2 = Samples.
 */
export interface TransportDisp {
  fmt: number;
  bpm: number;
  tsNum: number;
  tsDenom: number;
  /**
   * Bar-number offset (client-derived, not on the wire): REAPER's authoritative
   * bar from the `label` minus the bar this client would compute from projectSec.
   * Captures the project's "start measure" setting (e.g. a −1 start / count-in),
   * which is NOT encoded in projectSec/bpm. Added to the computed bar so the web
   * shows the same bar number as REAPER. 0 when unknown.
   */
  barOffset?: number;
  /**
   * REAPER "Project start time" offset in seconds (from GetProjectTimeOffset).
   * Added to the buffer-corrected projectSec for the time/samples readout so a
   * project that starts at −30 s shows negative times. 0 when unset.
   */
  startOffsetSec?: number;
  /** Host-chosen Opus bitrate in bits/s (the plugin's quality setting). For the
   *  format caption; 0/absent when unknown. */
  bitrate?: number;
}

/** Raw `tc` data packet shape (docs §2.1 / §7.7). */
export interface TimecodePacket {
  t: "tc";
  seq: number;
  streamSamples: number;
  projectSec: number;
  playing: boolean;
  /** Display context (format + musical context); the client formats the corrected position. */
  disp?: TransportDisp;
  /** Optional REAPER-exact position string (docs §7.7); shown verbatim only if no `disp`. */
  label?: string;
  /** Host-chosen listener latency/buffer in ms (docs §7.7). */
  latencyMs?: number;
}

/** One (streamSamples -> projectSec) pair as stamped by the server, kept in a
 *  short history so the AUDIBLE position can be looked up instead of guessed
 *  (see projectSecFromAnchors). Exported for the unit tests. */
export interface Anchor {
  /** Sequence number of the source `tc` packet (docs §2.1). */
  seq: number;
  streamSamples: number;
  projectSec: number;
  playing: boolean;
  /** Display context (format + musical), if supplied (docs §7.7). */
  disp?: TransportDisp;
  /** REAPER-exact position label, if the host supplied one (docs §7.7). */
  label?: string;
  /** Host-chosen listener latency/buffer in ms (docs §7.7), if supplied. */
  latencyMs?: number;
  /** performance.now() (ms) when this packet was received locally. */
  recvAtMs: number;
}

export interface TimecodeState {
  /** Smoothed project time of the audio currently audible, in seconds. */
  projectSec: number;
  /** Whether the transport is currently playing. */
  playing: boolean;
  /** Effective playout delay used for the estimate (ms). */
  playoutDelayMs: number;
  /** True once at least one `tc` packet has been processed. */
  hasSignal: boolean;
  /** Display context from the latest `tc` packet (docs §7.7); the UI formats `projectSec` with it. */
  disp?: TransportDisp;
  /** Optional REAPER-exact label (docs §7.7); shown verbatim only when no `disp`. */
  label?: string;
  /** Host-chosen listener latency/buffer in ms (docs §7.7); the UI applies it as the base playout delay. */
  latencyMs?: number;
}

/**
 * Tracks the streamSamples->projectSec map and produces a smooth, buffer-
 * corrected displayed project time. One instance per room connection.
 */
export class TimecodeTracker {
  /** Most recent anchor (newest `tc` packet). */
  private latest: Anchor | null = null;
  /** REVIEW 2.1: anchor HISTORY, oldest first. The audible position lies one
   *  playout delay in the PAST, so mapping it through the newest anchor's
   *  current slope was wrong across every transport edge and seek: at the play
   *  edge the readout jumped back by the latency, at the stop edge it jumped
   *  forward while half a second of audio was still to be heard, and a comment
   *  frozen in that window anchored up to `latencyMs` off — the very promise of
   *  §4. The server stamps each packet with an exactly matching
   *  (streamSamples, projectSec) pair; we keep them instead of throwing them
   *  away, and interpolate between the two anchors that BRACKET the audible
   *  position. PROTOCOL §4 asks for a map "from the most recent tc packets" —
   *  plural. */
  private anchors: Anchor[] = [];
  /** ~25 Hz packets: 128 anchors ≈ 5 s of history, comfortably above the
   *  largest selectable latency. */
  private static readonly MAX_ANCHORS = 128;

  /** Base playout delay (ms), user-tunable from the UI. */
  private basePlayoutDelayMs: number;
  /** Refinement (ms) measured from WebRTC stats; added to the base, smoothed. */
  private statsDelayMs: number | null = null;
  /** Previous cumulative jitter-buffer counters (windowed-mean baseline). */
  private prevJbDelay = 0;
  private prevJbEmitted = 0;

  /** When the WS-Opus player is the audible path, the WebRTC jitter-buffer has
   *  nothing to do with what is heard — the UI installs the WS chain's real
   *  depth here and the base+stats estimate is bypassed entirely. */
  private playoutOverrideMs: number | null = null;

  /** Anchors older than this stop extrapolating: if tc packets cease (paused
   *  DAW whose host stops processing, a stalled data channel on iOS, a dead
   *  stream), the clock FREEZES instead of counting into fiction forever. */
  private static readonly ANCHOR_STALE_MS = 3000;
  /** The playout delay actually in effect (ms), ramped toward base+stats so a
   *  changed setting/estimate glides into the display instead of stepping it.
   *  null until the first tick seeds it. */
  private appliedPlayoutMs: number | null = null;

  /** Smoothed displayed project time (sec) — what the UI shows. */
  private smoothedProjectSec = 0;
  /** performance.now() at which smoothedProjectSec was last updated. */
  private smoothedAtMs = 0;
  private initialized = false;

  /** Exponential smoothing factor for the stats-derived delay refinement. */
  private static readonly STATS_SMOOTHING = 0.2;

  // ---- Display clock (a forward-clocked PLL — see stepDisplayClock) ----------
  /**
   * Convergence time constant (sec) for the readout's proportional correction.
   * After packet jitter, a standing lag/lead decays as e^(-t/TAU): ~95 % gone in
   * ~3·TAU ≈ 1 s. Lower = snappier, higher = smoother; it does NOT affect the
   * monotonic guarantee (that comes from the rate clamp, below).
   */
  private static readonly CONVERGE_TAU_SEC = 0.35;
  /** Upper bound on the display rate (project-sec per wall-sec): caps how fast a
   *  lag is chased so a positive jitter spike ramps instead of lurching. */
  private static readonly MAX_RATE = 8.0;
  /** |target − shown| above this is a real seek / loop / first-lock → jump
   *  crisply. Well above any packet-arrival jitter (~40 ms) so jitter never
   *  trips it; this is the ONLY path that moves the readout backward. */
  private static readonly SNAP_SEC = 0.25;
  /** Per-frame dt is clamped to this (sec) so a throttled tab (rAF → ~1 fps)
   *  cannot integrate a giant single-frame step. */
  private static readonly MAX_DT_SEC = 0.25;
  /** Max rate (ms of playout delay per wall-sec) at which the APPLIED playout
   *  delay glides toward a changed setting/estimate. 400 ms/s keeps the implied
   *  display-target velocity well inside SNAP_SEC (≈0.14 s of standing error),
   *  so a host latency change re-aligns smoothly instead of lurching/snapping —
   *  and it mirrors the WebRTC buffer ramping to the new latency. */
  private static readonly PLAYOUT_RAMP_MS_PER_SEC = 400;

  constructor(playoutDelayMs: number = DEFAULT_PLAYOUT_DELAY_MS) {
    this.basePlayoutDelayMs = playoutDelayMs;
  }

  /** Decode/render slack beyond the jitter buffer itself (ms). Small and
   *  constant — the buffer dominates. */
  private static readonly DECODE_SLACK_MS = 30;

  /** Raw target playout delay (ms). REVIEW 2.1: this used to be
   *  `base + stats`, which counted the SAME buffer twice — the base configures
   *  RTCRtpReceiver.jitterBufferTarget (room.ts), and refineFromStats() then
   *  measures exactly that buffer again. At the 500 ms default the correction
   *  came out around 1000 ms instead of ~530, so the readout ran half a second
   *  BEHIND the audio and frozen comment anchors landed too early. The measured
   *  mean REPLACES the buffer portion (as the comment at refineFromStats always
   *  claimed); the base stays a floor so a still-filling buffer cannot make the
   *  display run ahead. */
  private get rawPlayoutDelayMs(): number {
    if (this.playoutOverrideMs !== null) return this.playoutOverrideMs;
    if (this.statsDelayMs === null) return this.basePlayoutDelayMs;
    return Math.max(
      this.basePlayoutDelayMs,
      this.statsDelayMs + TimecodeTracker.DECODE_SLACK_MS,
    );
  }

  /** Total playout delay (ms) currently in effect — the ramped value (falls back
   *  to the raw target before the first tick seeds the ramp). Drives both the
   *  display-target correction and the latency readout, so they stay consistent. */
  get playoutDelayMs(): number {
    return this.appliedPlayoutMs ?? this.rawPlayoutDelayMs;
  }

  private get playoutDelaySamples(): number {
    return (this.playoutDelayMs / 1000) * SAMPLE_RATE;
  }

  /** Tunable from the UI; the base (non-stats) component of the delay. */
  setBasePlayoutDelayMs(ms: number): void {
    this.basePlayoutDelayMs = Math.max(0, ms);
  }

  /** Install/clear the WS-path playout override (null = back to base+stats). */
  setPlayoutOverrideMs(ms: number | null): void {
    this.playoutOverrideMs = ms === null ? null : Math.max(0, ms);
  }

  /** Forget the WebRTC stats refinement (e.g. when the audible path switches:
   *  a stale muted-receiver measurement must not haunt the readout). */
  clearStatsRefinement(): void {
    this.statsDelayMs = null;
    this.prevJbDelay = 0;
    this.prevJbEmitted = 0;
  }

  /** True while the latest anchor is fresh AND says playing — extrapolation
   *  and the 1x project slope are only allowed then. */
  private effectivePlaying(): boolean {
    const a = this.latest;
    if (!a) return false;
    return a.playing && now() - a.recvAtMs < TimecodeTracker.ANCHOR_STALE_MS;
  }

  getBasePlayoutDelayMs(): number {
    return this.basePlayoutDelayMs;
  }

  /** Feed a `tc` packet (docs §2.1). Out-of-order/stale packets are ignored. */
  ingest(pkt: TimecodePacket): void {
    // Reject a corrupt packet whose numeric fields are non-finite (e.g. an
    // Infinity from a JSON overflow literal): a NaN/Inf would poison the display
    // clock with no recovery path. Keep the last good anchor instead.
    // `seq` belongs in this guard too: a non-finite seq makes every later
    // `pkt.seq < latest.seq` comparison false, permanently disabling the
    // reorder rejection below (REVIEW, minor finding on the same lines).
    if (
      !Number.isFinite(pkt.seq) ||
      !Number.isFinite(pkt.streamSamples) ||
      !Number.isFinite(pkt.projectSec)
    ) {
      return;
    }
    if (this.latest) {
      // A large backward jump in streamSamples means the HOST restarted the stream:
      // the plugin resets seq→0 and the server spins up a fresh publisher whose
      // sample count restarts near 0. Re-lock on the new stream instead of treating
      // every new packet as stale (which froze the readout at the old position).
      // streamSamples is the server's monotonic push count within one publisher, so
      // it only moves backward across a reconnect — a safe discriminator from
      // data-channel reordering (at most a few packets, ~120 ms).
      if (pkt.streamSamples < this.latest.streamSamples - SAMPLE_RATE) {
        this.latest = null;
        this.anchors = [];   // the old stream's map says nothing about the new one
        this.initialized = false;
      } else if (pkt.seq < this.latest.seq && pkt.streamSamples <= this.latest.streamSamples) {
        // Older than what we already have (lossy data channel may reorder).
        return;
      }
    }
    const anchor: Anchor = {
      seq: pkt.seq,
      streamSamples: pkt.streamSamples,
      projectSec: pkt.projectSec,
      playing: pkt.playing,
      disp: pkt.disp,
      label: pkt.label,
      latencyMs: pkt.latencyMs,
      recvAtMs: now(),
    };
    this.latest = anchor;
    this.anchors.push(anchor);
    if (this.anchors.length > TimecodeTracker.MAX_ANCHORS) this.anchors.shift();

    // (The start-measure bar offset is now computed plugin-side and arrives in
    // disp.barOffset — see web/.. vs StreamWorker. We no longer derive it here,
    // which was wrong once the position moved: the label was live but projectSec
    // was the delayed audio frame, so the derived offset drifted.)

    if (!this.initialized) {
      // Seed the smoothed readout at the first audible estimate so the display
      // does not visibly ramp up from zero.
      this.smoothedProjectSec = this.computeTargetProjectSec();
      this.smoothedAtMs = now();
      this.initialized = true;
    }
  }

  /**
   * Refine the playout delay from a WebRTC RTCRtpReceiver stats sample.
   * `jitterBufferDelay` is cumulative seconds spent in the jitter buffer and
   * `jitterBufferEmittedCount` the number of samples/frames emitted; their
   * ratio is the mean delay. We add the decode/render slack implicitly via the
   * base delay. Smoothed to avoid the readout twitching on every poll.
   */
  refineFromStats(jitterBufferDelaySec: number, jitterBufferEmittedCount: number): void {
    if (!(jitterBufferEmittedCount > 0) || !(jitterBufferDelaySec >= 0)) return;
    // WINDOWED mean: the spec counters are CUMULATIVE since receiver birth —
    // a lifetime mean lags a latency change by however long the session ran.
    if (jitterBufferEmittedCount < this.prevJbEmitted) {
      // Receiver reset: re-seed the window.
      this.prevJbDelay = jitterBufferDelaySec;
      this.prevJbEmitted = jitterBufferEmittedCount;
      return;
    }
    const dDelay = jitterBufferDelaySec - this.prevJbDelay;
    const dEmitted = jitterBufferEmittedCount - this.prevJbEmitted;
    this.prevJbDelay = jitterBufferDelaySec;
    this.prevJbEmitted = jitterBufferEmittedCount;
    if (dEmitted <= 0) return;
    const meanMs = (dDelay / dEmitted) * 1000;
    if (!Number.isFinite(meanMs) || meanMs < 0) return;
    // Treat the measured jitter-buffer mean as a *replacement* for the buffer
    // portion of the delay: keep the user base as a floor and add the measured
    // amount above any built-in expectation. Here we simply track the measured
    // mean directly as the refinement, smoothed.
    if (this.statsDelayMs === null) {
      this.statsDelayMs = meanMs;
    } else {
      this.statsDelayMs =
        this.statsDelayMs * (1 - TimecodeTracker.STATS_SMOOTHING) +
        meanMs * TimecodeTracker.STATS_SMOOTHING;
    }
  }

  /**
   * Compute the *target* audible project time directly from the anchors, with
   * no local-clock smoothing. Used to seed and to pull the smoothed value.
   */
  private audibleSample: number | null = null;
  setAudibleSample(sample: number | null): void {
    this.audibleSample = sample !== null && Number.isFinite(sample) ? sample : null;
  }

  private computeTargetProjectSec(): number {
    const a = this.latest;
    if (!a) return 0;
    if (this.audibleSample !== null) return this.mapSamplesToProjectSec(this.audibleSample);

    // Extrapolate the received stream-sample position to "now" using the local
    // clock: while playing, the source keeps consuming samples at SAMPLE_RATE.
    const elapsedMs = now() - a.recvAtMs;
    const advanceSamples = this.effectivePlaying() ? (elapsedMs / 1000) * SAMPLE_RATE : 0;
    const receivedStreamSamples = a.streamSamples + advanceSamples;

    // Back off by the playout delay to get what is audible right now.
    const audibleSamples = receivedStreamSamples - this.playoutDelaySamples;

    return this.mapSamplesToProjectSec(audibleSamples);
  }

  /**
   * Map a stream-sample position to project seconds, anchored on the latest
   * packet. While playing the slope is 1 sample : 1/SR sec (see
   * currentProjectSlopePerSec); while paused it is 0, freezing the readout.
   */
  private mapSamplesToProjectSec(streamSamples: number): number {
    if (!this.latest) return 0;
    return projectSecFromAnchors(
      this.anchors,
      streamSamples,
      this.currentProjectSlopePerSec() / SAMPLE_RATE,
    );
  }

  /**
   * Project slope in seconds-of-project per second-of-wall-time: pinned to 1.0
   * while playing at 1× (the project clock tracks real time), 0 while paused.
   *
   * The measured (prev,latest) slope was REMOVED. project_sec (from REAPER's
   * cursor) and streamSamples (the server's push count) ride slightly drifting
   * clocks, so their per-packet ratio is noisy. computeTargetProjectSec multiplies
   * that slope by the *playout-delay offset* (~32 000 samples at a 0.67 s buffer),
   * which amplified a few-percent slope wobble into multi-100 ms target swings —
   * tripping the PLL snap branch every packet and bouncing the readout backward.
   * Nominal 1× is exact for normal playback; true varispeed would need a heavily
   * smoothed slope, which we can add if it's ever required.
   */
  private currentProjectSlopePerSec(): number {
    return this.effectivePlaying() ? 1 : 0;
  }

  /**
   * Pure display-clock follower (unit-tested in isolation). Advances `prevShown`
   * one frame of `dtSec` toward the (jittery) `target`.
   *
   * The fix for the second/beat-boundary flicker: instead of a symmetric EMA
   * that follows `target` *downward* when packet-arrival jitter nudges it back,
   * the display runs as its own clock at the project `slope`, bent by a
   * proportional correction `err/TAU`, with the resulting rate hard-clamped to
   * [0, MAX_RATE]. Because rate ≥ 0 and dt ≥ 0, the increment is never negative
   * while playing → the displayed value (and thus every rendered digit) is
   * STRICTLY MONOTONIC NON-DECREASING. The convergence law is dL/dt = −L/TAU
   * (L = lag), so jitter is erased with no permanent lead/lag. The only path
   * that moves backward is the SNAP branch — a genuine seek/loop/first-lock,
   * where the audio jumps too. While paused the transport is frozen, so there is
   * nothing to smooth: we show the (stable) stop position directly — which also
   * means a stop never walks the PLL's tiny steady-state lead backward.
   */
  static stepDisplayClock(
    prevShown: number,
    target: number,
    dtSec: number,
    playing: boolean,
    slope: number,
  ): number {
    // Bad-input guards: one non-finite packet field (a NaN, or an Infinity from a
    // JSON overflow literal) must not permanently poison the clock. NaN fails every
    // comparison, so without these it would skip the snap re-lock and `prevShown +
    // NaN*dt` would stick forever. Hold the last good value; re-lock when finite.
    if (!Number.isFinite(target)) return Number.isFinite(prevShown) ? prevShown : 0;
    if (!Number.isFinite(prevShown)) return target; // target is finite here → re-lock

    if (!playing) return target; // frozen transport: show the stop position directly

    const dt = Math.min(Math.max(0, dtSec), TimecodeTracker.MAX_DT_SEC);
    const err = target - prevShown;

    if (Math.abs(err) > TimecodeTracker.SNAP_SEC) return target; // seek / loop / lock

    let rate = slope + err / TimecodeTracker.CONVERGE_TAU_SEC;
    if (!(rate > 0)) rate = 0; // ← monotonic guard (also neutralizes a NaN slope)
    else if (rate > TimecodeTracker.MAX_RATE) rate = TimecodeTracker.MAX_RATE;
    return prevShown + rate * dt; // step ≥ 0 always
  }

  /** Linear ramp of `current` toward `target`, moving at most `maxStep`. Used to
   *  glide the applied playout delay so a latency change doesn't step the display
   *  target past the snap threshold. Pure; unit-tested. */
  static rampToward(current: number, target: number, maxStep: number): number {
    const d = target - current;
    if (Math.abs(d) <= maxStep) return target;
    return current + Math.sign(d) * maxStep;
  }

  /** The latest display context (carries the plugin-provided barOffset/startOffsetSec). */
  private dispWithOffset(): TransportDisp | undefined {
    return this.latest?.disp;
  }

  /**
   * Advance the smoothed readout toward the freshly computed target using the
   * local clock, and return the current display state. Call once per animation
   * frame (or per UI tick).
   */
  tick(): TimecodeState {
    const playing = this.effectivePlaying();

    if (!this.initialized || !this.latest) {
      return {
        projectSec: 0,
        playing: false,
        playoutDelayMs: this.playoutDelayMs,
        hasSignal: false,
        disp: undefined,
        label: undefined,
        latencyMs: undefined,
      };
    }

    const t = now();
    const dtSec = (t - this.smoothedAtMs) / 1000;
    this.smoothedAtMs = t;

    // STALE anchor while it still said "playing": tc packets stopped but audio
    // may well continue. HOLD the shown position — recomputing the target from
    // the stale anchor would snap the readout BACKWARD by up to the stale
    // window (~3 s) and then jump forward again when packets resume.
    if (this.latest.playing && !playing) {
      return {
        projectSec: this.smoothedProjectSec,
        playing,
        playoutDelayMs: this.playoutDelayMs,
        hasSignal: true,
        disp: this.dispWithOffset(),
        label: this.latest.label,
        latencyMs: this.latest.latencyMs,
      };
    }

    // Glide the applied playout delay toward its target so a changed latency
    // setting (or the first WebRTC-stats sample) re-aligns the readout smoothly
    // instead of stepping the display target past the snap threshold.
    const rampStep =
      TimecodeTracker.PLAYOUT_RAMP_MS_PER_SEC * Math.min(Math.max(0, dtSec), TimecodeTracker.MAX_DT_SEC);
    this.appliedPlayoutMs = TimecodeTracker.rampToward(
      this.appliedPlayoutMs ?? this.rawPlayoutDelayMs,
      this.rawPlayoutDelayMs,
      rampStep,
    );

    const target = this.computeTargetProjectSec();

    // Advance the display clock toward the (jittery) target. The PLL runs the
    // readout forward at the project slope with a clamped proportional pull, so
    // it never ticks backward from packet-arrival jitter — the source of the
    // second/beat-boundary flicker — yet still converges with no standing lag.
    this.smoothedProjectSec = this.audibleSample !== null ? target : TimecodeTracker.stepDisplayClock(
      this.smoothedProjectSec,
      target,
      dtSec,
      playing,
      this.currentProjectSlopePerSec(),
    );

    return {
      projectSec: this.smoothedProjectSec,
      playing,
      playoutDelayMs: this.playoutDelayMs,
      hasSignal: true,
      disp: this.dispWithOffset(),
      label: this.latest.label,
      latencyMs: this.latest.latencyMs,
    };
  }

  /** Current displayed project time (sec) without advancing smoothing. */
  currentProjectSec(): number {
    return this.audibleSample !== null ? this.computeTargetProjectSec() : this.smoothedProjectSec;
  }

  /**
   * The latest REAPER-formatted transport label (docs §7.7), or undefined when
   * the host sends no label. Used to freeze a comment anchor with the same
   * verbatim text the listener currently sees.
   */
  currentLabel(): string | undefined {
    return this.audibleSample !== null
      ? formatPosition(this.computeTargetProjectSec(), this.dispWithOffset(), this.latest?.label)
      : this.latest?.label;
  }

  /** The latest §7.7 display context (with derived bar offset), for comment anchors/times. */
  currentDisp(): TransportDisp | undefined {
    return this.dispWithOffset();
  }
}

function now(): number {
  return typeof performance !== "undefined" ? performance.now() : Date.now();
}

/**
 * Format project seconds as HH:MM:SS.mmm (docs/brief: DAW-time display).
 * Negative project times (e.g. a project that starts at -30 s for a count-in)
 * are shown with a leading "-"; NaN clamps to 00:00:00.000.
 */
export function formatProjectTime(projectSec: number): string {
  if (!Number.isFinite(projectSec)) projectSec = 0;
  const totalMs = Math.round(Math.abs(projectSec) * 1000);
  const neg = projectSec < 0 && totalMs > 0; // no "-00:00:00.000" for tiny negatives
  const ms = totalMs % 1000;
  const totalSec = Math.floor(totalMs / 1000);
  const s = totalSec % 60;
  const m = Math.floor(totalSec / 60) % 60;
  const h = Math.floor(totalSec / 3600);
  const p2 = (n: number) => n.toString().padStart(2, "0");
  const p3 = (n: number) => n.toString().padStart(3, "0");
  return `${neg ? "-" : ""}${p2(h)}:${p2(m)}:${p2(s)}.${p3(ms)}`;
}

/**
 * Format a REAPER-style bars.beats.ticks string (1-based) from project seconds
 * and the musical context. ticks = hundredths of a beat. Assumes a constant
 * tempo from project start (tempo maps would drift slightly — acceptable for a
 * human-readable readout). Falls back to time if the context is unusable.
 */
export function formatBarsBeats(
  projectSec: number,
  bpm: number,
  tsNum: number,
  tsDenom: number,
  barOffset = 0,
): string {
  if (!(bpm > 0) || !(tsNum > 0) || !(tsDenom > 0)) return formatProjectTime(projectSec);
  const ppq = projectSec * (bpm / 60); // position in quarter notes (may be < 0)
  const qnPerBar = tsNum * (4 / tsDenom);
  const beatLenQn = 4 / tsDenom;
  if (!(qnPerBar > 0) || !(beatLenQn > 0)) return formatProjectTime(projectSec);
  const bar0 = Math.floor(ppq / qnPerBar);
  const inBar = ppq - bar0 * qnPerBar; // floor-based ⇒ always in [0, qnPerBar)
  const beat0 = Math.floor(inBar / beatLenQn);
  let ticks = Math.round((inBar / beatLenQn - beat0) * 100);
  if (ticks > 99) ticks = 99;
  if (ticks < 0) ticks = 0;
  // bar0 is 0-based from the timeline origin; +1 makes it 1-based, +barOffset
  // applies REAPER's start-measure setting (so a -1 start shows -1, 0, 1, …).
  const bar = bar0 + 1 + barOffset;
  return `${bar}.${beat0 + 1}.${ticks.toString().padStart(2, "0")}`;
}

/**
 * Format a project position for display, honoring the host's chosen unit (docs
 * §7.7). Computed from the (already buffer-corrected) `projectSec`, so it stays
 * sync'd to the heard audio in every format. Used for the transport readout AND
 * comment timestamps so they always match. A REAPER-exact `label` (when present
 * and no `disp`) is shown verbatim; otherwise the client formats it itself.
 */
export function formatPosition(projectSec: number, disp?: TransportDisp, label?: string): string {
  if (disp) {
    // Apply REAPER's project-start-time offset for time/samples (gives negative
    // times for a count-in). Bars use the musical bar offset instead (the time
    // offset doesn't move the musical grid).
    const off = disp.startOffsetSec ?? 0;
    switch (disp.fmt) {
      case 1:
        return formatBarsBeats(projectSec, disp.bpm, disp.tsNum, disp.tsDenom, disp.barOffset ?? 0);
      case 2:
        // REVIEW 2.1: REAPER counts samples in the PROJECT rate; SAMPLE_RATE is
        // the stream's 48 kHz. A 44.1 kHz project was 8.8 % off, a 96 kHz one
        // by a factor of two — in the transport readout and in every comment
        // timestamp. The plugin already sends the REAPER-exact string in the
        // same packet, so prefer it instead of computing a wrong number; the
        // fallback only applies when no label arrived (protocol §7.7 has no
        // project sample rate yet).
        if (label) return label;
        return Math.round((projectSec + off) * SAMPLE_RATE).toString();
      default:
        return formatProjectTime(projectSec + off);
    }
  }
  if (label) return label;
  return formatProjectTime(projectSec);
}

/**
 * Map a stream-sample position to project seconds using the ANCHOR HISTORY
 * (REVIEW 2.1). Pure, so it is unit-tested without a clock.
 *
 * - Inside the history: interpolate between the two anchors that bracket the
 *   position. Those are recorded (streamSamples, projectSec) pairs, so a
 *   transport edge or a seek that happened one buffer ago is reproduced
 *   exactly instead of being smeared with the slope in effect NOW.
 * - Older than the history (only right after a start, before it covers one
 *   playout delay): extrapolate from the oldest anchor with ITS transport
 *   state.
 * - Beyond the newest anchor: extrapolate with `slopeBeyond` — the only place
 *   where extrapolation is legitimate, because nothing newer exists yet.
 */
export function projectSecFromAnchors(
  anchors: readonly Anchor[],
  streamSamples: number,
  slopeBeyond: number,
): number {
  const n = anchors.length;
  if (n === 0) return 0;
  const newest = anchors[n - 1]!;

  if (streamSamples > newest.streamSamples || n === 1) {
    return newest.projectSec + (streamSamples - newest.streamSamples) * slopeBeyond;
  }

  const oldest = anchors[0]!;
  if (streamSamples < oldest.streamSamples) {
    const slope = oldest.playing ? 1 / SAMPLE_RATE : 0;
    return oldest.projectSec + (streamSamples - oldest.streamSamples) * slope;
  }

  let lo = 0;
  let hi = n - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (anchors[mid]!.streamSamples <= streamSamples) lo = mid;
    else hi = mid;
  }
  const a = anchors[lo]!;
  const b = anchors[hi]!;
  const span = b.streamSamples - a.streamSamples;
  if (span <= 0) return a.projectSec;
  const t = (streamSamples - a.streamSamples) / span;
  return a.projectSec + (b.projectSec - a.projectSec) * t;
}
